/** @format */

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useShare } from "../hooks/useShare";
import { formatGhanaCedis } from "../utils/formatUtils";
import EmailVerificationWarning from "../components/EmailVerificationWarning";
import CustomDropdown from "../components/CustomDropdown";
import { auth } from "../firebase/config";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  UserGroupIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import ConfirmationModal from "../components/ConfirmationModal";
import AIChatButton from '../components/AIChatButton';

const Profile: React.FC = () => {
  const { currentUser, updateProfile, uploadProfileImage, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const { copyToClipboard: shareCopyToClipboard } = useShare();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [editForm, setEditForm] = useState({
    name: currentUser?.name || "",
    email: currentUser?.email || "",
    telephone: currentUser?.telephone || "",
    program: currentUser?.program || "",
    registern: currentUser?.registern || "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Update form when currentUser changes
  useEffect(() => {
    setEditForm({
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      telephone: currentUser?.telephone || "",
      program: currentUser?.program || "",
      registern: currentUser?.registern || "",
    });
  }, [currentUser]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditForm({
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      telephone: currentUser?.telephone || "",
      program: currentUser?.program || "",
      registern: currentUser?.registern || "",
    });
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      await updateProfile({
        name: editForm.name,
        telephone: editForm.telephone,
        program: editForm.program as "ICAG" | "CITG" | null,
        registern: editForm.registern,
      });
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      await uploadProfileImage(file);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const copyToClipboard = async (text: string, field: string) => {
    const success = await shareCopyToClipboard(text, `${field} copied to clipboard!`);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const generateReferralLink = () => {
    if (!currentUser?.referralCode) return "";
    return `${window.location.origin}/signup?ref=${currentUser.referralCode}`;
  };

  const generateNewReferralCode = async () => {
    if (!currentUser) return;
    
    try {
      // Generate a new referral code
      const timestamp = Date.now().toString(36);
      const uidPart = currentUser.uid.substring(0, 6);
      const newReferralCode = `${uidPart}${timestamp}`.toUpperCase();
      
      // Update the user's referral code
      await updateProfile({
        referralCode: newReferralCode
      });
      
      toast.success("New referral code generated successfully!");
    } catch (error) {
      console.error('Error generating referral code:', error);
      toast.error("Failed to generate referral code");
    }
  };

  const handleConfirmSignOut = async () => {
    setIsProcessingAction(true);
    try {
      await logout();
      navigate("/");
    } finally {
      setIsProcessingAction(false);
      setShowSignOutConfirm(false);
    }
  };

  const handleConfirmDeleteAccount = async () => {
    setIsProcessingAction(true);
    try {
      await deleteAccount();
      navigate("/");
    } catch (e) {
      // error toast handled in deleteAccount
    } finally {
      setIsProcessingAction(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (changePasswordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      // Import the necessary Firebase functions
      const { reauthenticateWithCredential, EmailAuthProvider, updatePassword } = await import('firebase/auth');
      
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser?.email || '',
        changePasswordForm.currentPassword
      );
      
      await reauthenticateWithCredential(auth.currentUser!, credential);
      
      // Update password
      await updatePassword(auth.currentUser!, changePasswordForm.newPassword);
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setChangePasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Change password error:', error);
      let errorMessage = 'Failed to change password';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please sign in again to change your password';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (date: Date | string | number | any) => {
    if (!date) return "Not set";

    let dateObj: Date;

    try {
      // Handle Firestore timestamp objects
      if (date && typeof date === "object" && date.seconds) {
        dateObj = new Date(date.seconds * 1000);
      }
      // Handle regular Date objects
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Handle timestamp numbers (seconds or milliseconds)
      else if (typeof date === "number") {
        dateObj = new Date(date);
      }
      // Handle date strings
      else {
        dateObj = new Date(date);
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return "Invalid date";
      }

      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return "Invalid date";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Online":
        return "bg-green-100 text-green-800";
      case "Offline":
        return "bg-gray-100 text-gray-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "ERROR":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!currentUser) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Email Verification Warning */}
      <EmailVerificationWarning location="profile" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-4">
            {/* Profile Image */}
            <div className="relative">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                {currentUser.avatar ? (
                  <img 
                    src={currentUser.avatar} 
                    alt={currentUser.name || "Profile"} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-bold text-primary-600">
                    {currentUser.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                )}
              </div>
              
              {/* Upload Button Overlay */}
              <button
                onClick={triggerImageUpload}
                disabled={isUploadingImage}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 rounded-full flex items-center justify-center text-white shadow-lg transition-colors"
                title="Upload profile image"
              >
                {isUploadingImage ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <CameraIcon className="h-4 w-4" />
                )}
              </button>
            </div>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {currentUser.name || "User"}
              </h1>
              <p className="text-gray-600">{currentUser.email}</p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
            {!isEditing && (
              <button
                onClick={handleEdit}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edit Profile
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      handleInputChange("name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                ) : (
                  <div className="flex items-center text-gray-900">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
                    {currentUser.name || "Not set"}
                  </div>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editForm.telephone || ""}
                    onChange={(e) =>
                      handleInputChange("telephone", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <div className="flex items-center text-gray-900">
                    <PhoneIcon className="h-5 w-5 mr-2 text-gray-400" />
                    {currentUser.telephone || "Not set"}
                  </div>
                )}
              </div>

              {/* Program */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program
                </label>
                {isEditing ? (
                  <CustomDropdown
                    value={editForm.program}
                    onChange={(value) => handleInputChange("program", value)}
                    options={[
                      { value: "ICAG", label: "ICAG" },
                      { value: "CITG", label: "CITG" }
                    ]}
                    placeholder="Select Program"
                    className="w-full"
                  />
                ) : (
                  <div className="flex items-center text-gray-900">
                    <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-400" />
                    {currentUser.program || "Not set"}
                  </div>
                )}
              </div>

              {/* Registration Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Number
                </label>
                {isEditing ? (
                  <div>
                    <input
                      type="text"
                      value={editForm.registern}
                      onChange={(e) =>
                        handleInputChange("registern", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your ICAG/CITG registration number"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Your official registration number from ICAG or CITG (optional)
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-900">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-400" />
                    {currentUser.registern || "Not set"}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex space-x-2 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
            <button
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {showChangePassword ? 'Cancel' : 'Change Password'}
            </button>
          </div>
          
          {showChangePassword && (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={changePasswordForm.currentPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your current password"
                />
              </div>
              
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={changePasswordForm.newPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your new password"
                />
                <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters long</p>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={changePasswordForm.confirmPassword}
                  onChange={(e) => setChangePasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Confirm your new password"
                />
              </div>
              
              <div className="flex space-x-2 pt-2">
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isChangingPassword ? "Changing Password..." : "Change Password"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setChangePasswordForm({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Account Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Actions</h2>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
            <button
              onClick={() => setShowChangePassword(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Change Password
            </button>
            <button
              onClick={() => setShowSignOutConfirm(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isProcessingAction}
            >
              Sign Out
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isProcessingAction}
            >
              Delete Account
            </button>
          </div>
        </div>

        {/* Referral Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center mb-6">
            <UserGroupIcon className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Referral Program</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Referral Code */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Referral Code
              </label>
              {currentUser?.referralCode ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-2 font-mono text-sm">
                      {currentUser.referralCode}
                    </div>
                    <button
                      onClick={() => copyToClipboard(currentUser.referralCode || "", "Referral Code")}
                      className={`p-2 rounded-md transition-colors ${
                        copiedField === "Referral Code"
                          ? "bg-green-100 text-green-700"
                          : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                      }`}
                      title="Copy referral code"
                    >
                      {copiedField === "Referral Code" ? (
                        <CheckIcon className="h-4 w-4" />
                      ) : (
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <button
                    onClick={generateNewReferralCode}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Regenerate Code
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500">
                    No referral code yet
                  </div>
                  <button
                    onClick={generateNewReferralCode}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Generate Referral Code
                  </button>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {currentUser?.referralCode 
                  ? 'Share this code with friends to earn rewards'
                  : 'Generate a referral code to start earning rewards'
                }
              </p>
            </div>

            {/* Referral Link */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Referral Link
              </label>
              {currentUser?.referralCode ? (
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-2 font-mono text-xs truncate">
                    {generateReferralLink()}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generateReferralLink(), "Referral Link")}
                    className={`p-2 rounded-md transition-colors ${
                      copiedField === "Referral Link"
                        ? "bg-green-100 text-green-700"
                        : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                    }`}
                    title="Copy referral link"
                  >
                    {copiedField === "Referral Link" ? (
                      <CheckIcon className="h-4 w-4" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ) : (
                <div className="bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500">
                  Generate a referral code first
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {currentUser?.referralCode 
                  ? 'Share this link for easy signup'
                  : 'Create a referral code to get your personalized link'
                }
              </p>
            </div>
          </div>

          {/* Referral Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{currentUser.totalrefs || 0}</div>
              <div className="text-sm text-gray-600">Total Referrals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatGhanaCedis(currentUser.earnings || 0)}</div>
              <div className="text-sm text-gray-600">Total Earnings</div>
              {(currentUser.earnings || 0) > 0 && (
                <Link
                  to="/withdraw"
                  className="inline-block mt-2 px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors"
                >
                  Withdraw
                </Link>
              )}
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {currentUser.referredBy ? "Yes" : "No"}
              </div>
              <div className="text-sm text-gray-600">Referred By Someone</div>
            </div>
          </div>

          {/* Referral Info */}
          {currentUser?.referredBy && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <LinkIcon className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm text-blue-800">
                  You were referred by: <strong>{currentUser.referredBy}</strong>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                <AcademicCapIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Referrals</p>
                <p className="text-2xl font-bold text-gray-900">{currentUser.totalrefs || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mr-4">
                  <CurrencyDollarIcon className="h-6 w-6 text-secondary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-gray-900">{formatGhanaCedis(currentUser.earnings || 0)}</p>
                </div>
              </div>
              {(currentUser.earnings || 0) > 0 && (
                <Link
                  to="/withdraw"
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Withdraw
                </Link>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mr-4">
                <CalendarIcon className="h-6 w-6 text-accent-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Member Since</p>
                <p className="text-2xl font-bold text-gray-900">
                  {currentUser.createdAt ? (() => {
                    try {
                      const year = new Date(currentUser.createdAt).getFullYear();
                      return isNaN(year) ? "N/A" : year;
                    } catch (error) {
                      return "N/A";
                    }
                  })() : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={handleConfirmSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText={isProcessingAction ? "Signing out..." : "Sign Out"}
        cancelText="Cancel"
        confirmButtonColor="blue"
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDeleteAccount}
        title="Delete Account"
        message="This action is permanent and will delete your account and related data. Are you sure you want to continue?"
        confirmText={isProcessingAction ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmButtonColor="red"
      />

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default Profile;
